import FetchAccountFacilities from '@Application/Facilities/Facilities/Queries/FetchAccountFacilities';
import fetchAccountFacilities from '@Application/Facilities/Facilities/fetchAccountFacilities';
import FetchAuditEntries from '@Application/AuditTrail/Queries/FetchAuditEntries';
import fetchAuditEntries from '@Application/AuditTrail/fetchAuditEntries';
import FetchAccountUsers from '@Application/Users/Users/Queries/FetchAccountUsers';
import fetchAccountUsers from '@Application/Users/Users/fetchAccountUsers';
import EmptyState from '@Guidelines/Component/EmptyState';
import ErrorState from '@Guidelines/Component/ErrorState';
import { theme } from '@Guidelines/Theme/theme';
import AuditEntryCard from '@UI/Pages/Administration/AuditTrail/AuditEntryCard';
import auditTrailFiltersSignal, {
	setAuditTrailFilters,
} from '@UI/Pages/Administration/AuditTrail/Filters/Signals/auditTrailFiltersSignal';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type AuditEntryView from '@UI/Store/AuditTrail/Views/AuditEntryView';
import { css } from '@emotion/react';
import { css as toClassName } from '@emotion/css';
import { Building2, FileClock } from 'lucide-react';
import * as React from 'react';
import { useSignalValue } from '@verclam/pulsar';
import { CACHE_DISABLED, useDispatch, useStoreQuery } from '@verclam/synapse';
import { DataView as AuditTrailDataView, type DataViewPageEvent } from '@verclam/ui-kit';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	flex: 1;
	overflow: hidden;
`;

// Reused as a plain `className` for `DataView`'s `pt.grid`: `DataView` renders the cards it is handed
// straight into `.ui-kit-data-view-grid`, and that class only sets `--ui-kit-data-view-grid-gap` under
// its own `gutter` prop, which stays `0` otherwise — the cards would touch without this override.
const cardsGridStyle = toClassName`
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 0.875rem;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		grid-template-columns: 1fr;
	}
`;

/** Every user page this screen requests, wide enough to keep the `Utilisateur #42` fallback rare
 * without special-casing every id an old entry might reference. */
const ACTOR_LOOKUP_ROWS = 200;

/**
 * Server-paginated, server-filtered list of audit entries, rendered through `DataView` — no action of
 * any kind, at any permission level. Owns the fetch of the entries themselves, plus two
 * supporting catalogs it does not render on its own: the account's users (actor names on cards, and
 * the actor dropdown's options, read back by `index.tsx` through `useSelector`) and the account's
 * facilities (same reasoning, for agency names). `resourceId`/`beneficiaryId`/`facilityId` never
 * resolve to `{ id }` on the wire — there is no relation to follow, only a bare integer, hence the
 * resolvers below rather than a nested field.
 *
 * The branch order is deliberate: `error !== null` is checked before `data === null`, never the
 * reverse, so an absent `data` is never read as a failure (`guides/state-stores.md`, "Ce que
 * `useStoreQuery` ne garantit pas").
 */
const AuditTrailContent: React.FC = () => {
	const dispatch = useDispatch();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const filters = useSignalValue(auditTrailFiltersSignal);

	const {
		data: auditEntries,
		isLoading,
		error,
		refetch,
	} = useStoreQuery({
		selector: (state) => state.auditTrailStore.entries,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAuditEntries(new FetchAuditEntries(accountId, filters.queryParams)));
		},
		// `accountId` decides which tenant is queried; a filter change alone would not otherwise
		// re-trigger `query` since `deps` — not `stopOn` — drives the refetch under `CACHE_DISABLED`.
		deps: [filters.queryParams.toString(), accountId],
		cache: CACHE_DISABLED,
	});

	// No `cache` preset: `stopOn` reads a store that survives this component unmounting, so each
	// catalog is fetched once per session rather than once per mount.
	const { data: users } = useStoreQuery({
		selector: (state) => state.usersStore.users,
		stopOn: (value) => value !== null && value.accountId === accountId,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			const usersQueryParams = new URLSearchParams();
			usersQueryParams.set('page', '0');
			usersQueryParams.set('rows', String(ACTOR_LOOKUP_ROWS));

			return dispatch(fetchAccountUsers(new FetchAccountUsers(accountId, usersQueryParams)));
		},
		deps: [accountId],
	});

	const { data: facilities } = useStoreQuery({
		selector: (state) => state.accountFacilitiesStore.accountFacilities,
		stopOn: (value) => value !== null && value.accountId === accountId && !value.isRefreshRequested,
		query: () => {
			if (accountId === null) {
				return Promise.resolve();
			}

			return dispatch(fetchAccountFacilities(new FetchAccountFacilities(accountId)));
		},
		deps: [accountId],
	});

	const resolveActorLabel = React.useCallback(
		(actorUserId: number | null): string => {
			if (actorUserId === null) {
				return 'Acteur non identifié';
			}

			return users?.findById(actorUserId)?.fullName ?? `Utilisateur #${actorUserId}`;
		},
		[users],
	);

	const resolveFacilityLabel = React.useCallback(
		(facilityId: number | null): string | null => {
			if (facilityId === null) {
				return null;
			}

			return facilities?.findById(facilityId)?.name ?? `Agence #${facilityId}`;
		},
		[facilities],
	);

	const handlePage = React.useCallback((event: DataViewPageEvent) => {
		setAuditTrailFilters(auditTrailFiltersSignal.peek().setPageAndRows(event.page, event.rows));
	}, []);

	const renderAuditEntryCard = React.useCallback(
		(entry: AuditEntryView) => (
			<AuditEntryCard
				entry={entry}
				actorLabel={resolveActorLabel(entry.actorUserId)}
				facilityLabel={resolveFacilityLabel(entry.facilityId)}
			/>
		),
		[resolveActorLabel, resolveFacilityLabel],
	);

	// `lazy` is mandatory here: the list arrives already paginated by the server (`auditEntries.list`
	// is one page, `auditEntries.totalRecords` the true total), and without it `DataView`'s own
	// slicing would re-cut this already-cut page, hiding everything past the first one.
	const dataViewPt: React.ComponentProps<typeof AuditTrailDataView>['pt'] = React.useMemo(
		() => ({
			root: {
				style: {
					height: '100%',
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					overflow: 'hidden',
				},
			},
			grid: { className: cardsGridStyle },
			content: {
				style: {
					flex: 1,
					overflowY: 'auto',
				},
			},
			emptyMessage: {
				style: {
					height: '100%',
				},
			},
		}),
		[],
	);

	/**
	 * `null`-equivalent (blank) while the query is pending: `DataView` treats `loading` as an overlay,
	 * not as a branch — its content still renders on `pageItems.length === 0` alone, so an
	 * unconditional message would claim "Aucune entrée" before the answer is known. An empty state is
	 * an answer, and may only be drawn once one has arrived.
	 */
	const emptyMessage = React.useMemo(() => {
		const style = {
			height: '100%',
		};

		// Holds the frame's height so the list does not collapse under the kit's loading overlay, and
		// says nothing: any wording here is a claim about a result that has not arrived yet.
		if (isLoading) {
			return <div style={style} />;
		}

		return (
			<EmptyState
				title="Aucune entrée ne correspond à ces filtres."
				icon={<FileClock size={28} />}
				style={style}
			/>
		);
	}, [isLoading]);

	// Theoretical: `AUDIT_READ` is never carried by an account-less (platform) user, and the server
	// would refuse the call in `403` regardless. Written anyway, so it is not improvised at the
	// first entry that actually needs it.
	if (accountId === null) {
		return (
			<EmptyState
				title="Aucun compte associé."
				description="Votre compte n'est rattaché à aucun compte : le journal d'audit n'est pas accessible."
				icon={<Building2 size={28} />}
			/>
		);
	}

	if (error !== null) {
		return <ErrorState title="Impossible de charger le journal d'audit." onRetry={refetch} />;
	}

	return (
		<div css={containerStyle}>
			<AuditTrailDataView
				emptyMessage={emptyMessage}
				loading={isLoading}
				value={auditEntries?.list ?? []}
				layout="grid"
				dataKey="id"
				itemTemplate={renderAuditEntryCard}
				pt={dataViewPt}
				lazy
				paginator
				first={filters.page * filters.rows}
				rows={filters.rows}
				rowsPerPageOptions={[10, 25, 50, 100]}
				totalRecords={auditEntries?.totalRecords ?? 0}
				onPage={handlePage}
			/>
		</div>
	);
};

export default AuditTrailContent;
